// src/store/userSlice.ts
import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { bureauInfo } from "../types";
import { SessionKeys } from "../utils/constants";
import { getRequest } from "../helpers/api.helper";

interface BureauState {
  data: bureauInfo | null;
  loading: boolean;
  error: string | null;
}

const readStoredBureau = (): bureauInfo | null => {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(SessionKeys.bureauData);
    if (!raw) return null;
    let parsed: unknown = JSON.parse(raw);
    if (typeof parsed === "string") {
      try {
        parsed = JSON.parse(parsed);
      } catch {
        return null;
      }
    }
    if (parsed && typeof parsed === "object") {
      return parsed as bureauInfo;
    }
  } catch (error) {
    console.error("Failed to parse stored user data", error);
  }
  return null;
};

const initialState: BureauState = {
  data: readStoredBureau(),
  loading: false,
  error: null,
};

export const fetchBureauData = createAsyncThunk<
  bureauInfo,
  void,
  { rejectValue: string }
>("bureau/fetchBureauData", async (_, { rejectWithValue }) => {
  const token = localStorage.getItem("token");
  if (!token) {
    return rejectWithValue("Missing auth token");
  }
  try {
    const response = await getRequest("/bureau/me");
    if (!response.success) {
      return rejectWithValue("Failed to load user profile");
    }
    return response.data.bureau as bureauInfo;
  } catch (error: any) {
    const message =
      error?.response?.data?.message ||
      error?.message ||
      "Failed to fetch bureau";
    return rejectWithValue(message);
  }
});

const bureauSlice = createSlice({
  name: "bureau",
  initialState,
  reducers: {
    setBureau(state, action: PayloadAction<bureauInfo | null>) {
      state.data = action.payload;
      state.error = null;
    },
    clearBureau(state) {
      state.data = null;
      state.error = null;
    },
    setLoading(state, action: PayloadAction<boolean>) {
      state.loading = action.payload;
    },
    setError(state, action: PayloadAction<string | null>) {
      state.error = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchBureauData.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchBureauData.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload;
      })
      .addCase(fetchBureauData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload ?? "Unable to fetch user data";
      });
  },
});

export const { setBureau, clearBureau, setLoading, setError } =
  bureauSlice.actions;
export default bureauSlice.reducer;
